/**
 * Krok 3: KPI Metriky, v14
 * Segment selector (přesunutý sem) + RRS (s auto-fill benchmarku) + měsíční ad spend + trend tržeb
 */
import React, { useState } from 'react';
import { TooltipLabel } from './TooltipLabel';
import { validateKpiInputs } from '../utils/inputValidation';

function UnknownCheckbox({ id, checked, onChange }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', marginTop: '0.35rem', fontSize: '0.85rem', color: 'var(--text-dim)', cursor: 'pointer' }}>
      <input type="checkbox" id={id} checked={checked} onChange={e => onChange(e.target.checked)} />
      Nevím / nesledujeme
    </label>
  );
}

const TREND_OPTIONS = [
  { value: 'accelerating',  label: 'Zrychlující růst',  desc: 'Tržby meziměsíčně akcelerují' },
  { value: 'stable_growth', label: 'Stabilní růst',     desc: 'Pravidelný, konzistentní růst' },
  { value: 'slowing',       label: 'Zpomalující růst',  desc: 'Stále rosteme, ale pomaleji' },
  { value: 'flat',          label: 'Stagnace',          desc: 'Tržby zhruba stejné jako před 6 měsíci' },
  { value: 'declining',     label: 'Pokles',            desc: 'Tržby meziměsíčně klesají' },
];

export function Step2_KPIs({ wizardState, setWizardState, onNext, onBack }) {
  const [kpiForm, setKpiForm] = useState({ ...wizardState.kpi });
  const segmentId = wizardState.segment_id || wizardState.qualitative.segment_id || '';
  const [trend, setTrend] = useState(wizardState.qualitative.revenue_trend || '');
  const [trendError, setTrendError] = useState(false);

  const updateKpi = (field, value) => setKpiForm(prev => ({ ...prev, [field]: value }));

  const n = v => Number(v) || 0;

  // Benchmark data from wizardState (set in Step0)
  const benchmarks = wizardState.segmentBenchmarks;
  const segmentName = segmentId; // for display

  // Derivovaný ROAS z monthly_ad_spend
  const derivedRoas = !kpiForm.monthly_ad_spend_unknown && n(kpiForm.monthly_ad_spend) > 0 && n(wizardState.finance.revenue) > 0
    ? ((n(wizardState.finance.revenue) / 12) / n(kpiForm.monthly_ad_spend)).toFixed(1)
    : null;

  // Q3M YoY computed values
  const q3mPrev = n(wizardState.finance.revenue_last_3m_prev_year);
  const q3mCurr = n(wizardState.finance.revenue_last_3m);
  const q3mYoy = q3mPrev > 0 && q3mCurr > 0
    ? ((q3mCurr - q3mPrev) / q3mPrev * 100).toFixed(1)
    : null;
  const annualYoy = n(wizardState.finance.revenue_prev_year) > 0 && n(wizardState.finance.revenue) > 0
    ? (((n(wizardState.finance.revenue) - n(wizardState.finance.revenue_prev_year)) / n(wizardState.finance.revenue_prev_year)) * 100).toFixed(1)
    : null;
  const momentumDiff = q3mYoy !== null && annualYoy !== null ? Number(q3mYoy) - Number(annualYoy) : 0;

  const handleNext = () => {
    if (!trend) { setTrendError(true); return; }
    setWizardState(prev => ({
      ...prev,
      kpi: { ...kpiForm },
      qualitative: { ...prev.qualitative, revenue_trend: trend },
    }));
    onNext();
  };

  return (
    <div className="step-container">
      <h2>Metriky byznysu</h2>
      <p className="step-description">
        Pár metrik o vašem byznysu. U některých vám pro srovnání ukážeme průměr ve vašem segmentu.
      </p>

      <div className="edit-form">

        {/* RRS */}
        <div className="form-group" style={{ background: 'rgba(107,63,160,0.08)', border: '2px solid var(--purple-bright)', borderRadius: '10px', padding: '1rem', marginBottom: '1.5rem' }}>
          <TooltipLabel
            label="Tržby od vracejících se zákazníků"
            tooltip="Kolik procent vašich celkových tržeb pochází od zákazníků, kteří u vás již dříve nakoupili. Najdete v analytice jako 'returning vs. new customers'. Příklad: pokud máte roční tržby 10 mil. Kč a 4 mil. pochází od vracejících se zákazníků, vaše RRS je 40 %."
          />
          {!kpiForm.rrs_unknown ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="number"
                  value={kpiForm.rrs}
                  onChange={e => updateKpi('rrs', e.target.value)}
                  placeholder="35"
                  min="0"
                  max="100"
                  style={{ maxWidth: '120px' }}
                />
                <span style={{ fontWeight: 600, color: 'var(--star-white)' }}>%</span>
              </div>
            </>
          ) : (
            <small style={{ color: 'var(--text-dim)', fontStyle: 'italic' }}>
              Počítáme s výchozími 30 %. Je to odborný předpoklad, ne doložený průměr oboru,
              přesnější výsledek dá vaše skutečná hodnota.
            </small>
          )}
          <UnknownCheckbox id="unknown-rrs" checked={!!kpiForm.rrs_unknown} onChange={v => updateKpi('rrs_unknown', v)} />
        </div>

        {/* Monthly Ad Spend */}
        <div className="form-group" style={{ marginBottom: '1.5rem' }}>
          <TooltipLabel
            label="Měsíční výdaje na reklamu"
            tooltip="Celkové měsíční výdaje na online reklamu BEZ DPH, Meta Ads, Google Ads, Heureka, zbozi.cz, influenceři atd. Z tohoto čísla automaticky dopočítáme efektivitu reklamy (ROAS). Příklad: pokud utratíte 100 000 Kč/měsíc za reklamu a měsíční tržby jsou 500 000 Kč, váš ROAS je 5x."
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="number"
              value={kpiForm.monthly_ad_spend}
              onChange={e => updateKpi('monthly_ad_spend', e.target.value)}
              disabled={kpiForm.monthly_ad_spend_unknown}
              placeholder="150 000"
              min="0"
              style={{ maxWidth: '160px', opacity: kpiForm.monthly_ad_spend_unknown ? 0.4 : 1 }}
            />
            <span style={{ color: 'var(--text-dim)', fontSize: '0.9rem' }}>Kč / měsíc (bez DPH)</span>
          </div>
          {!kpiForm.monthly_ad_spend_unknown && derivedRoas && (
            <small>
              Váš blended ROAS: <strong>{derivedRoas}×</strong>
            </small>
          )}
          <UnknownCheckbox id="unknown-monthly_ad_spend" checked={!!kpiForm.monthly_ad_spend_unknown} onChange={v => updateKpi('monthly_ad_spend_unknown', v)} />
        </div>

        {/* Unit Economics (volitelné) */}
        <div className="form-group" style={{ marginBottom: '1.5rem' }}>
          <TooltipLabel
            label="Unit economics (volitelné)"
            tooltip="Pokud znáte hodnotu zákazníka (LTV), náklady na jeho získání (CAC) a míru opakovaných nákupů, zadejte je. Zpřesní hodnocení kvality vašeho byznysu. Pokud neznáte, nevadí, použijeme oborové průměry."
          />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem', marginTop: '0.5rem' }}>
            <div>
              <label style={{ fontSize: '0.82rem', color: 'var(--text-dim)', marginBottom: '0.3rem', display: 'block' }}>
                LTV (Kč)
              </label>
              <input
                type="number"
                value={kpiForm.ltv}
                onChange={e => updateKpi('ltv', e.target.value)}
                disabled={kpiForm.ltv_unknown}
                placeholder="1500"
                min="0"
                style={{ opacity: kpiForm.ltv_unknown ? 0.4 : 1 }}
              />
              <UnknownCheckbox id="unknown-ltv" checked={!!kpiForm.ltv_unknown} onChange={v => updateKpi('ltv_unknown', v)} />
            </div>
            <div>
              <label style={{ fontSize: '0.82rem', color: 'var(--text-dim)', marginBottom: '0.3rem', display: 'block' }}>
                CAC (Kč)
              </label>
              <input
                type="number"
                value={kpiForm.cac}
                onChange={e => updateKpi('cac', e.target.value)}
                disabled={kpiForm.cac_unknown}
                placeholder="500"
                min="0"
                style={{ opacity: kpiForm.cac_unknown ? 0.4 : 1 }}
              />
              <UnknownCheckbox id="unknown-cac" checked={!!kpiForm.cac_unknown} onChange={v => updateKpi('cac_unknown', v)} />
            </div>
            <div>
              <label style={{ fontSize: '0.82rem', color: 'var(--text-dim)', marginBottom: '0.3rem', display: 'block' }}>
                Opakované nákupy (%)
              </label>
              <input
                type="number"
                value={kpiForm.repeat_purchase_rate}
                onChange={e => updateKpi('repeat_purchase_rate', e.target.value)}
                disabled={kpiForm.repeat_purchase_rate_unknown}
                placeholder="30"
                min="0" max="100"
                style={{ opacity: kpiForm.repeat_purchase_rate_unknown ? 0.4 : 1 }}
              />
              <UnknownCheckbox id="unknown-repeat" checked={!!kpiForm.repeat_purchase_rate_unknown} onChange={v => updateKpi('repeat_purchase_rate_unknown', v)} />
            </div>
          </div>
          {!kpiForm.ltv_unknown && !kpiForm.cac_unknown && n(kpiForm.ltv) > 0 && n(kpiForm.cac) > 0 && (
            <small style={{ marginTop: '0.35rem', display: 'block' }}>
              LTV/CAC poměr: <strong>{(n(kpiForm.ltv) / n(kpiForm.cac)).toFixed(1)}×</strong>
            </small>
          )}
        </div>

        {/* Q3M YoY Comparison */}
        <div className="form-group" style={{ marginBottom: '1.5rem' }}>
          <TooltipLabel
            label="YoY srovnání posledních 3 měsíců (volitelné)"
            tooltip="Zadejte tržby za poslední 3 měsíce a za stejné 3 měsíce loňského roku. Díky tomu zjistíme krátkodobý momentum vašeho byznysu, kam se posouvá a jak se liší od ročního trendu. Příklad: leden-březen 2026 vs. leden-březen 2025."
          />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: '0.75rem', alignItems: 'start', marginTop: '0.5rem' }}>
            <div>
              <label style={{ fontSize: '0.82rem', color: 'var(--text-dim)', marginBottom: '0.3rem', display: 'block' }}>
                Stejné 3 měsíce loni (Kč)
              </label>
              <input
                type="number"
                value={wizardState.finance.revenue_last_3m_prev_year || ''}
                onChange={e => setWizardState(prev => ({
                  ...prev,
                  finance: { ...prev.finance, revenue_last_3m_prev_year: e.target.value }
                }))}
                placeholder="např. 5 000 000"
                min="0"
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: '1.7rem', gap: '0.1rem', minWidth: '2.2rem' }}>
              {q3mYoy !== null && (
                <span style={{ fontSize: '0.7rem', fontWeight: 700, color: q3mYoy >= 0 ? 'var(--color-success)' : 'var(--color-danger)', whiteSpace: 'nowrap' }}>
                  {q3mYoy >= 0 ? '+' : ''}{q3mYoy} %
                </span>
              )}
              <span style={{ color: 'rgba(155,89,212,0.4)' }}>→</span>
            </div>
            <div>
              <label style={{ fontSize: '0.82rem', color: 'var(--text-dim)', marginBottom: '0.3rem', display: 'block' }}>
                Poslední 3 měsíce letos (Kč)
              </label>
              <input
                type="number"
                value={wizardState.finance.revenue_last_3m || ''}
                onChange={e => setWizardState(prev => ({
                  ...prev,
                  finance: { ...prev.finance, revenue_last_3m: e.target.value }
                }))}
                placeholder="např. 6 500 000"
                min="0"
              />
            </div>
          </div>
          {q3mYoy !== null && annualYoy !== null && (
            <div style={{
              marginTop: '0.75rem', padding: '0.65rem 0.85rem',
              background: 'rgba(107, 63, 160, 0.08)',
              border: '1px solid rgba(155, 89, 212, 0.15)',
              borderRadius: 8, fontSize: '0.85rem',
            }}>
              <span style={{ color: 'var(--text-dim)' }}>
                Q3M YoY: <strong>{q3mYoy >= 0 ? '+' : ''}{q3mYoy} %</strong>
                {' '} vs. roční YoY: <strong>{annualYoy >= 0 ? '+' : ''}{annualYoy} %</strong>
              </span>
            </div>
          )}
        </div>

        {/* Cross-validace KPI */}
        {(() => {
          const kpiWarnings = validateKpiInputs(kpiForm, wizardState.finance.revenue, benchmarks);
          if (!kpiWarnings.length) return null;
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', marginBottom: '0.5rem' }}>
              {kpiWarnings.map((w, i) => (
                <div key={i} style={{
                  padding: '0.45rem 0.75rem',
                  background: w.severity === 'high' ? 'rgba(220, 38, 38, 0.08)' : 'rgba(245, 158, 11, 0.08)',
                  border: `1px solid ${w.severity === 'high' ? 'rgba(220, 38, 38, 0.25)' : 'rgba(245, 158, 11, 0.25)'}`,
                  borderRadius: 8, fontSize: '0.82rem',
                  color: w.severity === 'high' ? 'var(--color-danger)' : 'var(--gold)',
                }}>
                  {w.message}
                </div>
              ))}
            </div>
          );
        })()}

        {/* Revenue Trend */}
        <div className="form-group">
          <label style={{ fontWeight: 600, color: 'var(--purple-light)' }}>
            Jak se vyvíjejí tržby za posledních 6 měsíců?
          </label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginTop: '0.5rem' }}>
            {TREND_OPTIONS.map(opt => (
              <button
                key={opt.value}
                type="button"
                onClick={() => { setTrend(opt.value); setTrendError(false); }}
                style={{
                  padding: '0.55rem 1rem',
                  border: `2px solid ${trend === opt.value ? 'var(--purple-bright)' : 'rgba(155,89,212,0.15)'}`,
                  borderRadius: '8px',
                  background: trend === opt.value ? 'rgba(107,63,160,0.15)' : 'var(--purple-dark)',
                  cursor: 'pointer',
                  textAlign: 'left',
                  width: '100%',
                }}
              >
                <span style={{ fontSize: '0.9rem', fontWeight: trend === opt.value ? 700 : 400, color: 'var(--star-white)' }}>
                  {trend === opt.value ? '● ' : '○ '}{opt.label}
                  <span style={{ fontWeight: 400, fontSize: '0.82rem', color: 'var(--text-dim)', marginLeft: '0.5rem' }}>
                    {opt.desc}
                  </span>
                </span>
              </button>
            ))}
          </div>
          {trendError && <small className="chyba-pole" style={{ display: 'block', marginTop: '0.4rem' }}>Vyberte prosím, jak se vyvíjejí vaše tržby.</small>}
        </div>

      </div>

      <div className="button-group">
        <button onClick={onBack} className="btn-secondary">Zpět</button>
        <button onClick={handleNext} className="btn-primary">Pokračovat</button>
      </div>
    </div>
  );
}
